import { useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import {
  FileDropZone,
  type FileDropZoneHandle,
} from "@/components/common/FileDropZone";
import { BoxHeader } from "@/components/design/BoxHeader";
import { InlineEdit } from "@/components/design/InlineEdit";
import { SearchableSelect } from "@/components/design/SearchableSelect";
import { IndustriesDocument } from "@/graphql/generated/operations";
import type { FieldOption } from "@/lib/field-util";
import { RASTER_IMAGE_ACCEPT } from "@/lib/upload-limits";
import { CompanyLogo } from "./CompanyLogo";
import type { CompanyLogoValue } from "./company-logo";
import type { CompanyLogoEditing } from "./use-company-logo-editing";
import type { CompanyProfileEditing } from "./use-company-profile-editing";
import { ImageUpload } from "@/components/common/ImageUpload";

// The identity block of /c/:id (the company page): the logo, the name, the
// industry and the size. ONE component for both modes, because they are the same four facts.
//
// EDITING HAPPENS ON THE THING ITSELF. There used to be two more sections underneath this page —
// "Company logo" and "Company profile" — that redrew these four facts as form fields, so an admin
// who turned editing on saw the name twice, the industry twice and the size twice on one screen and
// had to work out which copy was the live one. Both sections are gone. In edit mode the header's
// own values become the controls: the logo is a button that opens the picker, the name and the size
// are <InlineEdit>s, and the industry is its picker. Nothing moves and nothing is duplicated.
//
// AN UNSET FIELD IS ABSENT IN VIEW MODE AND REACHABLE IN EDIT MODE. Those are two different jobs and
// they used to be confused: the subtitle printed "Industry not stated" to every passing visitor,
// which is a fact nobody asked for on a showcase page, while an admin had no way to reach the field
// from the header at all. Now a visitor sees only what the company actually stated — no industry and
// no size means no subtitle line whatsoever — and an admin sees a placeholder standing exactly where
// the value will be, which is itself the control that sets it.
//
// INDUSTRY IS A CATALOGUE, NOT FREE TEXT. It is the curated Industry reference entity shared with
// Experience (see company-profile-draft.ts). <InlineEdit> has no way to express "one of a curated
// list" — it is an input, and typing a name that is not in the catalogue would have to be either
// silently dropped or invented as a new industry — so this field stays a <SearchableSelect> and only
// takes on the plain, ring-less look of its neighbours. It is the one control here that is not the
// same primitive as the rest, and it is that on purpose.

/** Everything the header renders, in the shape the page's query returns it. */
export interface CompanyIdentitySource {
  name: string;
  employeeCount?: number | null;
  industry?: {
    id: string;
    name?: string | null;
    localizedName?: { localized?: string | null } | null;
  } | null;
  logo?: CompanyLogoValue | null;
}

interface CompanyIdentityProps {
  company: CompanyIdentitySource;
  profile: CompanyProfileEditing | null;
  logo: CompanyLogoEditing | null;
  busy?: boolean;
}

/**
 * The company's identity AS A CARD IN THE RAIL, the shape a person's identity already has on
 * /save/profile (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2). It used to be a wide header strip across the top of the page, which is
 * why the logo read as loose furniture floating above the sections rather than as the company's
 * own portrait: nothing enclosed it, and the two facts beside it ran the full width of the page.
 *
 * ONE SHAPE, NOT A VARIANT. There is exactly one caller, so a `header`/`card` prop would be a
 * switch with a dead position — the very thing that rotted CompanyProfileForm's `plain` variant.
 *
 * In edit mode the card IS the form: the name, the industry and the size are edited where they are
 * read, and the logo is the control (see CompanyLogoField). There is no second copy of these three
 * fields anywhere on the page.
 */
export function CompanyIdentity({
  company,
  profile,
  logo,
  busy = false,
}: CompanyIdentityProps) {
  const intl = useIntl();
  const editing = profile !== null;
  const industries = useQuery(IndustriesDocument, { skip: !editing });
  const industryOptions = useMemo<FieldOption[]>(
    () =>
      (industries.data?.industries.industries ?? [])
        .map((industry) => ({
          value: industry.id,
          label:
            industry.localizedName?.localized ?? industry.name ?? industry.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [industries.data],
  );
  // A company can carry an industry the catalogue no longer offers. Prepending the stored one keeps
  // the select able to show what is actually set, instead of falling back to "No industry" and
  // inviting the admin to overwrite a value nobody meant to change.
  const industryChoices = useMemo<FieldOption[]>(() => {
    const storedId = company.industry?.id;
    if (
      !storedId ||
      industryOptions.some((option) => option.value === storedId)
    )
      return industryOptions;
    const label =
      company.industry?.localizedName?.localized ||
      company.industry?.name ||
      storedId;
    return [{ value: storedId, label }, ...industryOptions];
  }, [industryOptions, company.industry]);

  const nameLabel = intl.formatMessage({
    id: "company.admin.field.name",
    defaultMessage: "Name",
  });
  const industryLabel = intl.formatMessage({
    id: "company.admin.field.industry",
    defaultMessage: "Industry",
  });
  const employeeCountLabel = intl.formatMessage({
    id: "company.admin.field.employeeCount",
    defaultMessage: "Employees",
  });
  const industryUnset = intl.formatMessage({
    id: "company.page.industry.unset",
    defaultMessage: "Industry not specified",
  });
  const employeeCountUnset = intl.formatMessage({
    id: "company.page.employeeCount.unset",
    defaultMessage: "Number",
  });
  const storedIndustry =
    company.industry?.localizedName?.localized ||
    company.industry?.name ||
    null;
  const storedEmployeeCount = company.employeeCount;

  return (
    <div id="company-card" className="box bg-surface p-6 text-center">
      <BoxHeader
        title={
          <FormattedMessage
            id="company.page.card.title"
            defaultMessage="Company card"
          />
        }
        className="text-left"
      />
      <div className="flex flex-col items-center gap-3">
        {/* CompanyLogo rather than a new Avatar size rung: Avatar stops at h-10 and widening it is a
            shared-primitive change. It draws the same monogram tokens, so the fallback reads
            identically to every other avatar in the app — and /c now draws the same component at
            its own size, so the two surfaces cannot disagree about what a company looks like.
            In edit mode the very same component becomes the control (see CompanyLogoField): there
            is no second, square-cropped preview of the logo anywhere, so what the admin clicks is
            what the page shows. */}
        {logo ? (
          <CompanyLogoField
            logo={logo}
            name={profile?.draft.name || company.name}
            busy={busy}
          />
        ) : (
          <CompanyLogo id="company-logo" logo={company.logo} name={company.name} size="page" />
        )}
        {/* `truncate` in view mode only: it sets white-space:nowrap and overflow:hidden, which
            would clip the wrapping field rather than the read-only name it was written for. */}
        <h1
          id="company-heading"
          className={`w-full smycles-page-title text-ink ${profile ? "" : "truncate"}`}
        >
          {profile ? (
            <InlineEdit
              id="company-name"
              ariaLabel={nameLabel}
              value={profile.draft.name}
              onChange={(next) => profile.set("name", next)}
              editable={!busy}
              wrap
              placeholder={nameLabel}
              className="smycles-page-title"
              inputClassName="smycles-page-title"
            />
          ) : (
            company.name
          )}
        </h1>
        {profile ? (
          <div className="flex w-full flex-col items-center gap-1">
            <SearchableSelect
              id="company-industry"
              className="min-w-0"
              value={profile.draft.industryId}
              onChange={(next) => profile.set("industryId", next)}
              options={industryChoices}
              disabled={busy}
              variant="plain"
              placeholder={industryUnset}
              emptyOptionLabel={intl.formatMessage({
                id: "company.admin.field.industryPlaceholder",
                defaultMessage: "No industry",
              })}
              searchPlaceholder={intl.formatMessage({
                id: "company.admin.field.industrySearch",
                defaultMessage: "Search industries",
              })}
              searchAriaLabel={intl.formatMessage({
                id: "company.admin.field.industrySearch",
                defaultMessage: "Search industries",
              })}
              noResultsLabel={intl.formatMessage({
                id: "company.admin.field.industryNoResults",
                defaultMessage: "No industry matches",
              })}
              ariaLabel={industryLabel}
            />
            {/* Still an integer-only, non-negative field: `type`/`min` give the native control its
                shape and companyDraftError refuses anything else — a decimal, an exponent or a
                minus — before the round trip. */}
            {/* THE UNIT IS THE LABEL, and the field is as wide as a headcount. A bare "234" is a
                number with no noun, and a full-width box for three digits was the loudest thing on
                the old registration form. So the word view mode already prints stands right where
                it stands there, and edit mode reads the same sentence with the digits made
                editable. */}
            <span className="flex items-baseline justify-center gap-1">
              <InlineEdit
                id="company-employee-count"
                ariaLabel={employeeCountLabel}
                value={profile.draft.employeeCount}
                onChange={(next) => profile.set("employeeCount", next)}
                editable={!busy}
                type="number"
                min={0}
                clearable={false}
                placeholder={employeeCountUnset}
                className="smycles-field-value"
                inputClassName="smycles-field-value"
              />
              <span className="smycles-field-value text-ink-muted">
                <FormattedMessage
                  id="company.page.employeeCount.unit"
                  defaultMessage="{count, plural, one {employee} other {employees}}"
                  values={{ count: Number(profile.draft.employeeCount) || 0 }}
                />
              </span>
            </span>
          </div>
        ) : storedIndustry != null || storedEmployeeCount != null ? (
          <p id="company-facts" className="smycles-field-meta text-ink-muted">
            {storedIndustry}
            {storedIndustry != null && storedEmployeeCount != null
              ? " · " /* i18n-allow */
              : null}
            {storedEmployeeCount != null ? (
              <FormattedMessage
                id="company.page.employeeCount"
                defaultMessage="{count, plural, one {# employee} other {# employees}}"
                values={{ count: storedEmployeeCount }}
              />
            ) : null}
          </p>
        ) : null}
      </div>
      {logo ? <CompanyLogoNotes logo={logo} /> : null}
    </div>
  );
}

function CompanyLogoField({
  logo,
  name,
  busy,
}: {
  logo: CompanyLogoEditing;
  name: string;
  busy: boolean;
}) {
  const intl = useIntl();
  const zoneRef = useRef<FileDropZoneHandle>(null);

  return (
    <FileDropZone
      ref={zoneRef}
      inputId="company-logo-input"
      variant="overlay"
      accept={RASTER_IMAGE_ACCEPT}
      multiple={false}
      disabled={busy}
      onFiles={(files) => logo.choose(files)}
      className="flex-shrink-0 rounded-full"
    >
      <ImageUpload
        editable
        busy={busy}
        // Mirrors the person's photo control in Save · Basics: the instruction lives in the overlay
        // that appears on hover, not in a sentence underneath. `remove` is deliberately omitted —
        // a company logo is replaced, not taken away (Kevin, 2026-08-21).
        hoverLabel={intl.formatMessage({
          id: "company.admin.logo.changeHover",
          defaultMessage: "Change logo",
        })}
        ariaLabel={
          logo.shown
            ? intl.formatMessage({
                id: "company.admin.logo.change",
                defaultMessage: "Change the company logo",
              })
            : intl.formatMessage({
                id: "company.admin.logo.choose",
                defaultMessage: "Upload a company logo",
              })
        }
        onActivate={() => zoneRef.current?.openPicker()}
      >
        <CompanyLogo id="company-logo" logo={logo.shown} name={name} size="page" />
      </ImageUpload>
    </FileDropZone>
  );
}

/**
 * What the logo control has to say: how to use it, how to get rid of the image, and whether the
 * bytes on screen have reached genesis yet.
 *
 * Under the header rather than beside the logo, because a refusal is a whole sentence ("brand.pdf
 * cannot be used as a logo. Choose a PNG, …") and an eighty-pixel column is no place to read one.
 *
 * THERE IS NO REMOVE CONTROL. A logo is replaced by uploading another one; it cannot be taken away
 * and left empty (Kevin, 2026-08-21). Everything here is a statement, not a second route to an
 * operation the bar already offers.
 */
function CompanyLogoNotes({ logo }: { logo: CompanyLogoEditing }) {
  return (
    <>
      {/* NOTHING DESCRIPTIVE UNDER THE LOGO. The instruction is in the hover overlay and the file
          limits are not worth a permanent line — a file that breaks them is refused by name, with
          the reason, in the message below. What remains here is state: unsaved, refused, saved. */}
      {/* NO "not saved yet" LINE FOR THE LOGO. The picked image is already on screen in place of the
          stored one, which says "this is a preview" more directly than a sentence under it can — and
          since the whole edit view became one draft, an unsaved logo is no longer the exception that
          needed calling out. The SaveBar carries the fact that something is unsaved, for everything
          at once. Kevin, 2026-08-27. */}
      {logo.message ? (
        <p id="company-logomessage" className="smycles-field-meta mt-2 text-ink">{logo.message}</p>
      ) : null}
      {!logo.message && logo.pickedSaved ? (
        <p id="company-logosaved" className="smycles-field-meta mt-2 text-ink-muted">
          <FormattedMessage
            id="company.admin.logo.saved"
            defaultMessage="Logo saved."
          />
        </p>
      ) : null}
    </>
  );
}
